import { createReservoir } from '@/api/layers';
import { ReservoirCreate } from '@/types/layer';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useState } from 'react';

interface CreateLayerModalProps {
  open: boolean;
  onClose: () => void;
  fieldId: string;
  onLayerCreated?: () => void;
}

export const CreateLayerModal: React.FC<CreateLayerModalProps> = ({
  open,
  onClose,
  fieldId,
  onLayerCreated,
}) => {
  const [formData, setFormData] = useState<ReservoirCreate>({
    name: '',
    description: '',
    type: '',
    field_id: fieldId,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Reservoir name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createReservoir(formData);
      onLayerCreated?.();
      handleClose();
    } catch (err) {
      setError('Failed to create reservoir');
      console.error('Error creating reservoir:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: '',
      field_id: fieldId,
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Create New Reservoir</Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reservoir Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="oil">Oil</MenuItem>
                <MenuItem value="gas">Gas</MenuItem>
                <MenuItem value="oil and gas">Oil and Gas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim()}
        >
          {loading ? <CircularProgress size={20} /> : 'Create Reservoir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
